import React, { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { fetchCollection } from '../lib/cms'
import ContactForm from '../components/ContactForm'
import { imageUrl } from '../lib/images'
import { useCart } from '../context/CartContext'

// Standard fallback data matcher præcis CMS opdateringen
const DEFAULT_STARTERS = [
  {
    id: 'starter-rejecocktail',
    title: 'Rejecocktail',
    item_number: '602',
    description: 'Klassisk rejecocktail m. asparges, rød dressing, flutes og smør',
    price: '43 kr. pr. kuvert',
    price_num: 43,
    sort_order: 1,
    active: true,
    image: ['/_assets/rejecocktail-klassisk-realistisk.jpg'],
  },
  {
    id: 'starter-tunmousse',
    title: 'Tunmousse',
    item_number: '605',
    description: 'På fad inkl. dressing – m. flutes og smør',
    price: '43 kr. pr. kuvert',
    price_num: 43,
    sort_order: 2,
    active: true,
    image: ['/_assets/tunmousse-klassisk-fad-realistisk.jpg'],
  },
]

const DEFAULT_MEATS = [
  {
    id: 'meat-pattegris',
    title: 'Helstegt pattegris',
    item_number: '1',
    description: 'Helstegt pattegris marineret med vores specielle krydderblanding. Grilles og udskæres på stedet af grillmesteren.',
    price_extra: '125 kr. pr. kuvert',
    sort_order: 1,
    is_pattegris: true,
    active: true,
    image: ['/_assets/img-4538.jpeg'],
  },
  {
    id: 'meat-oksemoerbrad',
    title: 'Oksemørbrad',
    item_number: '2',
    description: 'Ekstra mør og saftig helstegt oksemørbrad, krydret med havsalt og friskkværnet peber.',
    price_extra: 'Luksusvalg (+10 kr. pr. kuvert)',
    sort_order: 2,
    is_premium: true,
    active: true,
    image: ['/_assets/oksemorbrad-anretning.jpg'],
  },
  {
    id: 'meat-porchetta',
    title: 'Porchetta',
    item_number: '3',
    description: 'Italiensk inspireret krydret rullesteg med sprød svær og friske krydderurter.',
    price_extra: 'Inkluderet i buffetvalg',
    sort_order: 3,
    active: true,
    image: ['/_assets/porchetta-helstegt-dansk.jpg'],
  },
  {
    id: 'meat-kyllingebryst',
    title: 'Kyllingebryst',
    item_number: '4',
    description: 'Møre og saftige kyllingebrystfileter med grillkrydderi og milde urter.',
    price_extra: 'Inkluderet i buffetvalg',
    sort_order: 4,
    active: true,
    image: ['/_assets/marineret-kyllingebryst-grillet.jpg'],
  },
  {
    id: 'meat-flaeskesteg',
    title: 'Flæskesteg',
    item_number: '5',
    description: 'Klassisk mør flæskesteg med ekstra sprøde svær og havsalt.',
    price_extra: 'Inkluderet i buffetvalg',
    sort_order: 5,
    active: true,
    image: ['/_assets/flaeskesteg-uden-mennesker.jpg'],
  },
]

const DEFAULT_POTATOES = [
  {
    id: 'potatoes-floede',
    title: 'Flødekartofler',
    item_number: '1',
    description: 'Klassiske cremede flødekartofler tilberedt med hvidløg og muskatnød.',
    price_extra: '35 kr. pr. kuvert',
    sort_order: 1,
    active: true,
    image: ['/_assets/cremede-floedekartofler.jpg'],
  },
  {
    id: 'potatoes-ovn',
    title: 'Ovnkartofler',
    item_number: '2',
    description: 'Små møre ovnstegte kartofler vendt med olivenolie, havsalt og krydderurter.',
    price_extra: '40 kr. pr. kuvert',
    sort_order: 2,
    active: true,
    image: ['/_assets/ovnstegte-smaae-kartofler.jpg'],
  },
  {
    id: 'potatoes-hasselback',
    title: 'Hasselbackkartofler',
    item_number: '3',
    description: 'Sprøde og gyldne rillede kartofler bagt med smør, havsalt og paprika.',
    price_extra: '40 kr. pr. kuvert',
    sort_order: 3,
    active: true,
    image: ['/_assets/hasselback-kartofler.jpg'],
  },
]

const DEFAULT_SALADS = [
  {
    id: 'salads-groen',
    title: 'Almindelig grøn salat',
    item_number: '100',
    description: 'Frisk blandet grøn salat',
    price_extra: '20 kr. pr. kuvert',
    ingredients: [
      'Icebergsalat',
      'Ærter',
      'Tomat',
      'Gulerod',
      'Majs',
      'Agurk',
      'Croutoner/peanuts',
    ],
    sort_order: 1,
    active: true,
    image: ['/_assets/stemningsfuld-buffet-med-helstegt-steg.png'],
  },
  {
    id: 'salads-sommer',
    title: 'Sommersalat',
    item_number: '111',
    description: 'Frisk og let sommersalat med melon og ristede sesamfrø',
    price_extra: '30 kr. pr. kuvert',
    ingredients: [
      'Vandmelon og honningmelon',
      'Rucola',
      'Ristede sesamfrø',
      'Tomater',
      'Agurk',
    ],
    sort_order: 2,
    active: true,
    image: ['/_assets/sommersalat-melon.jpg'],
  },
]

export default function OrderGuidePage() {
  const { setMenuInCart, openCart, cart } = useCart()
  const [currentStep, setCurrentStep] = useState(1)
  const [guestCount, setGuestCount] = useState(35)

  // CMS Collections
  const [starters, setStarters] = useState([])
  const [meats, setMeats] = useState([])
  const [potatoes, setPotatoes] = useState([])
  const [salads, setSalads] = useState([])
  const [loading, setLoading] = useState(true)

  // Selected State
  const [selectedStarters, setSelectedStarters] = useState([])
  const [selectedMeats, setSelectedMeats] = useState([])
  const [selectedPotatoes, setSelectedPotatoes] = useState([])
  const [selectedSalads, setSelectedSalads] = useState([])

  // Load menu options from CMS
  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true)
        const [startersData, meatsData, potatoesData, saladsData] = await Promise.all([
          fetchCollection('menu_starters'),
          fetchCollection('menu_meats'),
          fetchCollection('menu_potatoes'),
          fetchCollection('menu_salads'),
        ])

        const sortByOrder = (a, b) => (a.sort_order || 99) - (b.sort_order || 99)
        const filterActive = (list, fallback) => {
          if (!list || list.length === 0) return fallback
          const activeOnly = list.filter((item) => item.active !== false)
          return activeOnly.length > 0 ? activeOnly.sort(sortByOrder) : fallback
        }

        setStarters(filterActive(startersData, DEFAULT_STARTERS))
        setMeats(filterActive(meatsData, DEFAULT_MEATS))
        setPotatoes(filterActive(potatoesData, DEFAULT_POTATOES))
        setSalads(filterActive(saladsData, DEFAULT_SALADS))
      } catch (err) {
        console.error('Fejl ved indlæsning af menu data:', err)
        setStarters(DEFAULT_STARTERS)
        setMeats(DEFAULT_MEATS)
        setPotatoes(DEFAULT_POTATOES)
        setSalads(DEFAULT_SALADS)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [])

  // Helper toggle functions for multi-select
  const toggleItem = (item, list, setList) => {
    const exists = list.some((i) => i.id === item.id)
    if (exists) {
      setList(list.filter((i) => i.id !== item.id))
    } else {
      setList([...list, item])
    }
  }

  // 5 Trin (Sovs er fjernet)
  const steps = [
    { number: 1, title: 'Forret', desc: 'Valgfrit', icon: 'fa-shrimp' },
    { number: 2, title: 'Kød', desc: 'Gris & kød', icon: 'fa-drumstick-bite' },
    { number: 3, title: 'Kartofler', desc: 'Kartoffelretter', icon: 'fa-bowl-rice' },
    { number: 4, title: 'Salater', desc: 'Friske salater', icon: 'fa-leaf' },
    { number: 5, title: 'Opsummering', desc: 'Pris & bestilling', icon: 'fa-file-invoice-dollar' },
  ]

  const totalSelections =
    selectedStarters.length +
    selectedMeats.length +
    selectedPotatoes.length +
    selectedSalads.length

  // Beregning af samlet pris pr. kuvert og i alt
  const priceCalculation = useMemo(() => {
    // 1. Forret: 43 kr. pr. forret pr. kuvert
    const starterPrice = selectedStarters.length * 43

    // 2. Kød:
    // Tjek om helstegt pattegris er valgt
    const hasPattegris = selectedMeats.some(
      (m) => m.title?.toLowerCase().includes('pattegris') || m.is_pattegris
    )
    const hasPremiumMeat = selectedMeats.some(
      (m) => m.title?.toLowerCase().includes('mørbrad') || m.is_premium
    )

    let meatPrice = 0
    if (selectedMeats.length > 0) {
      if (hasPattegris) {
        // Helstegt pattegris grundpris: 125 kr. pr. kuvert (hvis tilbehør er valgt, ellers 149 kr.)
        const hasSides = selectedPotatoes.length > 0 || selectedSalads.length > 0
        const basePattegris = hasSides ? 125 : 149
        const extraMeatCount = selectedMeats.length - 1
        meatPrice = basePattegris + extraMeatCount * 20
      } else {
        // Almindelig stegebuffet
        const count = selectedMeats.length
        if (count === 1) meatPrice = 85
        else if (count === 2) meatPrice = 95
        else if (count === 3) meatPrice = 105
        else meatPrice = 105 + (count - 3) * 10
      }

      // Luksuskød tillæg (f.eks. Oksemørbrad +10 kr.)
      if (hasPremiumMeat) {
        meatPrice += 10
      }
    }

    // 3. Tilbehør (Kartofler og Salater):
    // Flødekartofler: 35 kr., Ovnkartofler: 40 kr., Hasselbackkartofler: 40 kr.
    const potatoesPrice = selectedPotatoes.reduce((sum, p) => {
      const isFloede = p.title?.toLowerCase().includes('fløde')
      return sum + (isFloede ? 35 : 40)
    }, 0)

    // Salater: Almindelig grøn salat: 20 kr., Sommersalat: 30 kr. (10 kr. dyrere)
    const saladsPrice = selectedSalads.reduce((sum, s) => {
      const isSommer = s.title?.toLowerCase().includes('sommer')
      return sum + (isSommer ? 30 : 20)
    }, 0)
    const hasSummerSalad = selectedSalads.some(
      (s) => s.title?.toLowerCase().includes('sommer') || s.is_premium
    )

    const sidesPrice = potatoesPrice + saladsPrice
    const hasSides = selectedPotatoes.length > 0 || selectedSalads.length > 0

    // Samlet kuvertpris
    const pricePerGuest = starterPrice + meatPrice + sidesPrice
    const parsedGuestCount = Math.max(1, Number(guestCount) || 1)
    const totalPrice = pricePerGuest * parsedGuestCount

    return {
      starterPrice,
      meatPrice,
      sidesPrice,
      potatoesPrice,
      saladsPrice,
      pricePerGuest,
      totalPrice,
      hasPattegris,
      hasPremiumMeat,
      hasSummerSalad,
      hasSides,
    }
  }, [selectedStarters, selectedMeats, selectedPotatoes, selectedSalads, guestCount])

  const handleNext = () => {
    if (currentStep < 5) {
      setCurrentStep((prev) => prev + 1)
      window.scrollTo({ top: 180, behavior: 'smooth' })
    }
  }

  const handlePrev = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1)
      window.scrollTo({ top: 180, behavior: 'smooth' })
    }
  }

  const handleReset = () => {
    setSelectedStarters([])
    setSelectedMeats([])
    setSelectedPotatoes([])
    setSelectedSalads([])
    setCurrentStep(1)
  }

  const handleOrderNow = () => {
    if (currentStep !== 5) {
      setCurrentStep(5)
    }
    setTimeout(() => {
      const formEl = document.getElementById('bestillings-formular')
      if (formEl) {
        formEl.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
    }, 100)
  }

  return (
    <div className="py-10 sm:py-16">
      {/* Top Banner */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 mb-10">
        <div className="text-center max-w-3xl mx-auto">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-[#241F1C] tracking-tight">
            Sammensæt din personlige festmenu
          </h1>
          <p className="mt-2 text-xs sm:text-base text-[#6E635C] leading-relaxed">
            Vælg nemt dine favoritter trin for trin. Den samlede pris beregnes løbende, og dine valg
            samles til sidst i en uforpligtende bestillingsforespørgsel.
          </p>
        </div>

        {/* Step Navigation Bar (Desktop & Tablet) */}
        <div className="mt-10 hidden sm:grid grid-cols-5 gap-2 bg-white p-3 rounded-2xl border border-[#E6DFD5] shadow-sm">
          {steps.map((s) => {
            const isCompleted = currentStep > s.number
            const isCurrent = currentStep === s.number
            return (
              <button
                key={s.number}
                type="button"
                onClick={() => setCurrentStep(s.number)}
                className={`flex flex-col items-center text-center p-3 rounded-xl transition-all cursor-pointer ${
                  isCurrent
                    ? 'bg-[#241F1C] text-white shadow-sm'
                    : isCompleted
                    ? 'bg-[#FAF8F5] text-[#241F1C] hover:bg-[#F3EFEA]'
                    : 'text-[#6E635C] hover:text-[#241F1C] hover:bg-[#FAF8F5]'
                }`}
              >
                <div
                  className={`text-xs font-bold mb-1.5 flex items-center justify-center w-5 h-5 rounded-full ${
                    isCurrent
                      ? 'bg-white text-[#241F1C]'
                      : isCompleted
                      ? 'bg-[#C25E27] text-white'
                      : 'bg-[#E6DFD5] text-[#6E635C]'
                  }`}
                >
                  {isCompleted ? <i className="fa-solid fa-check text-[10px]" aria-hidden="true" /> : s.number}
                </div>
                <span className="text-xs font-bold tracking-tight">{s.title}</span>
                <span
                  className={`text-[10px] ${
                    isCurrent ? 'text-white/70' : 'text-[#6E635C]'
                  }`}
                >
                  {s.desc}
                </span>
              </button>
            )
          })}
        </div>

        {/* Mobile Step Header */}
        <div className="mt-6 sm:hidden bg-white p-4 rounded-xl border border-[#E6DFD5] shadow-sm flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="font-bold text-sm text-white bg-[#241F1C] w-7 h-7 rounded-full flex items-center justify-center">
              {currentStep}/5
            </div>
            <div>
              <div className="text-xs text-[#6E635C] uppercase font-semibold">Aktuelt trin</div>
              <div className="text-sm font-bold text-[#241F1C]">
                {steps[currentStep - 1]?.title}
              </div>
            </div>
          </div>
          <div className="text-right">
            <div className="text-xs font-bold text-[#C25E27]">
              {priceCalculation.pricePerGuest > 0
                ? `${priceCalculation.pricePerGuest} kr./kuvert`
                : `${totalSelections} valg`}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          {/* Main Step Cards (8 cols on desktop) */}
          <div className="lg:col-span-8">
            {loading ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-[#E6DFD5]">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-3 border-[#C25E27] border-t-transparent" />
                <p className="mt-3 text-sm text-[#6E635C]">Henter menukort fra Overgaard Pattegrise...</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* TRIN 1: FORRETTER */}
                {currentStep === 1 && (
                  <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E6DFD5] shadow-sm">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-6 border-b border-[#E6DFD5]">
                      <div>
                        <span className="text-xs uppercase font-bold text-[#241F1C] tracking-wider">
                          Trin 1 af 4 • Valgfrit
                        </span>
                        <h2 className="text-xl sm:text-2xl font-bold text-[#241F1C]">Vælg Forretter</h2>
                        <p className="text-sm text-[#6E635C] mt-1">
                          Serveres på fad med flutes og smør (43 kr. pr. kuvert). Du kan vælge én, begge eller springe forret over.
                        </p>
                      </div>
                      {selectedStarters.length > 0 && (
                        <button
                          type="button"
                          onClick={() => setSelectedStarters([])}
                          className="text-xs text-[#6E635C] hover:text-[#C25E27] underline self-start sm:self-auto cursor-pointer"
                        >
                          Fravælg forretter
                        </button>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      {starters.map((item) => {
                        const isSelected = selectedStarters.some((i) => i.id === item.id)
                        const itemImg = Array.isArray(item.image) ? item.image[0] : item.image
                        return (
                          <div
                            key={item.id}
                            onClick={() => toggleItem(item, selectedStarters, setSelectedStarters)}
                            className={`relative rounded-xl p-5 border-2 transition-all cursor-pointer flex flex-col justify-between ${
                              isSelected
                                ? 'border-[#C25E27] bg-[#FAF8F5] shadow-sm'
                                : 'border-[#E6DFD5] bg-white hover:border-[#DDD5C9] hover:bg-[#FAF8F5]/40'
                            }`}
                          >
                            <div className="flex items-start gap-3.5">
                              {itemImg && (
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden shrink-0 border border-[#E6DFD5] bg-[#FAF8F5]">
                                  <img
                                    src={imageUrl(itemImg, 'width=240,quality=85,format=auto')}
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                {item.item_number && (
                                  <span className="text-[11px] font-bold text-[#C25E27] block">
                                    Nr. {item.item_number}
                                  </span>
                                )}
                                <h3 className="font-bold text-base sm:text-lg text-[#241F1C]">{item.title}</h3>
                                {item.description && (
                                  <p className="text-xs text-[#6E635C] mt-1 leading-relaxed">
                                    {item.description}
                                  </p>
                                )}
                              </div>
                              <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                                  isSelected ? 'bg-[#C25E27] text-white' : 'border border-[#DDD5C9] bg-white text-transparent'
                                }`}
                              >
                                <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                              </div>
                            </div>

                            <div className="mt-4 pt-3 border-t border-[#E6DFD5] flex items-center justify-between text-xs font-semibold">
                              <span className="text-[#C25E27] font-bold">43 kr. pr. kuvert</span>
                              <span className={isSelected ? 'text-[#C25E27] font-bold' : 'text-[#6E635C]'}>
                                {isSelected ? 'Valgt' : 'Tryk for at vælge'}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <div className="mt-8 pt-6 border-t border-[#E6DFD5] flex items-center justify-between">
                      <span className="text-xs text-[#6E635C]">
                        {selectedStarters.length === 0
                          ? 'Ingen forret valgt (kan springes over)'
                          : `${selectedStarters.length} forret(ter) valgt (+${selectedStarters.length * 43} kr./kuvert)`}
                      </span>
                      <button
                        type="button"
                        onClick={handleNext}
                        className="inline-flex items-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-5 py-3 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer shadow-sm"
                      >
                        <span>Videre til Kød (Trin 2)</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}

                {/* TRIN 2: KØD */}
                {currentStep === 2 && (
                  <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E6DFD5] shadow-sm">
                    <div className="pb-6 border-b border-[#E6DFD5]">
                      <span className="text-xs uppercase font-bold text-[#241F1C] tracking-wider">
                        Trin 2 af 4 • Vælg kød
                      </span>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#241F1C]">Vælg Kød</h2>
                      <p className="text-sm text-[#6E635C] mt-1">
                        Vælg helstegt pattegris eller sammensæt din stegebuffet. Du kan frit vælge 1 eller flere slags kød.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                      {meats.map((item) => {
                        const isSelected = selectedMeats.some((i) => i.id === item.id)
                        const itemImg = Array.isArray(item.image) ? item.image[0] : item.image
                        return (
                          <div
                            key={item.id}
                            onClick={() => toggleItem(item, selectedMeats, setSelectedMeats)}
                            className={`relative rounded-xl p-5 border-2 transition-all cursor-pointer flex flex-col justify-between ${
                              isSelected
                                ? 'border-[#C25E27] bg-[#FAF8F5] shadow-sm'
                                : 'border-[#E6DFD5] bg-white hover:border-[#DDD5C9] hover:bg-[#FAF8F5]/40'
                            }`}
                          >
                            <div className="flex items-start gap-3.5">
                              {itemImg && (
                                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden shrink-0 border border-[#E6DFD5] bg-[#FAF8F5]">
                                  <img
                                    src={imageUrl(itemImg, 'width=240,quality=85,format=auto')}
                                    alt={item.title}
                                    className="w-full h-full object-cover"
                                    loading="lazy"
                                  />
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                {item.item_number && (
                                  <span className="text-[11px] font-bold text-[#C25E27] block">
                                    Nr. {item.item_number}
                                  </span>
                                )}
                                <h3 className="font-bold text-base sm:text-lg text-[#241F1C]">{item.title}</h3>
                                {item.description && (
                                  <p className="text-xs text-[#6E635C] mt-1.5 leading-relaxed">
                                    {item.description}
                                  </p>
                                )}
                              </div>
                              <div
                                className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                                  isSelected ? 'bg-[#C25E27] text-white' : 'border border-[#DDD5C9] bg-white text-transparent'
                                }`}
                              >
                                <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                              </div>
                            </div>

                            <div className="mt-5 pt-3 border-t border-[#E6DFD5] flex items-center justify-between text-xs font-semibold">
                              <span className="text-[#C25E27]">
                                {item.title?.toLowerCase().includes('pattegris')
                                  ? '125 kr. pr. kuvert m. tilbehør'
                                  : item.title?.toLowerCase().includes('mørbrad')
                                  ? 'Luksuskød (+10 kr.)'
                                  : 'Inkluderet i buffet'}
                              </span>
                              <span className={isSelected ? 'text-[#C25E27] font-bold' : 'text-[#6E635C]'}>
                                {isSelected ? 'Valgt' : 'Vælg kød'}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <div className="mt-8 pt-6 border-t border-[#E6DFD5] flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="inline-flex items-center gap-2 bg-[#FAF8F5] hover:bg-[#F3EFEA] text-[#241F1C] border border-[#E6DFD5] px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer"
                      >
                        <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                        <span>Tilbage</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleNext}
                        className="inline-flex items-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-5 py-3 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer shadow-sm"
                      >
                        <span>Videre til Kartofler (Trin 3)</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}

                {/* TRIN 3: KARTOFLER */}
                {currentStep === 3 && (
                  <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E6DFD5] shadow-sm">
                    <div className="pb-6 border-b border-[#E6DFD5]">
                      <span className="text-xs uppercase font-bold text-[#241F1C] tracking-wider">
                        Trin 3 af 4 • Vælg kartofler
                      </span>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#241F1C]">Vælg Kartofler</h2>
                      <p className="text-sm text-[#6E635C] mt-1">
                        Vælg dine favoritter blandt vores veltilberedte kartoffelretter. Indgår i tilbehørspakken.
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-5 mt-6">
                      {potatoes.map((item) => {
                        const isSelected = selectedPotatoes.some((i) => i.id === item.id)
                        const itemImg = Array.isArray(item.image) ? item.image[0] : item.image
                        return (
                          <div
                            key={item.id}
                            onClick={() => toggleItem(item, selectedPotatoes, setSelectedPotatoes)}
                            className={`group relative rounded-xl p-4 sm:p-5 border-2 transition-all cursor-pointer flex flex-col justify-between ${
                              isSelected
                                ? 'border-[#C25E27] bg-[#FAF8F5] shadow-sm'
                                : 'border-[#E6DFD5] bg-white hover:border-[#DDD5C9] hover:bg-[#FAF8F5]/40'
                            }`}
                          >
                            <div>
                              {itemImg && (
                                <div className="w-full h-32 sm:h-36 rounded-lg overflow-hidden border border-[#E6DFD5] bg-[#FAF8F5] relative mb-3.5">
                                  <img
                                    src={imageUrl(itemImg, 'width=350,quality=85,format=auto')}
                                    alt={item.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                    loading="lazy"
                                  />
                                  <div
                                    className={`absolute top-2.5 right-2.5 w-6 h-6 rounded-full flex items-center justify-center transition-colors shadow-sm ${
                                      isSelected
                                        ? 'bg-[#C25E27] text-white'
                                        : 'border border-[#DDD5C9] bg-white/95 text-transparent'
                                    }`}
                                  >
                                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                                  </div>
                                </div>
                              )}

                              <div className="flex items-center justify-between gap-2">
                                {item.item_number && (
                                  <span className="text-[11px] font-bold text-[#C25E27] block">
                                    Nr. {item.item_number}
                                  </span>
                                )}
                                {!itemImg && (
                                  <div
                                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                                      isSelected ? 'bg-[#C25E27] text-white' : 'border border-[#DDD5C9] bg-white text-transparent'
                                    }`}
                                  >
                                    <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                                  </div>
                                )}
                              </div>
                              <h3 className="font-bold text-base text-[#241F1C] mt-1 break-words">{item.title}</h3>
                              {item.description && (
                                <p className="text-xs text-[#6E635C] mt-1.5 leading-relaxed">
                                  {item.description}
                                </p>
                              )}
                            </div>

                            <div className="mt-5 pt-3 border-t border-[#E6DFD5] flex items-center justify-between text-xs font-semibold text-[#6E635C] gap-2">
                              <span className="text-[#C25E27] font-semibold whitespace-nowrap">
                                {item.title?.toLowerCase().includes('fløde')
                                  ? '35 kr. pr. kuvert'
                                  : item.price_extra && !item.price_extra.includes('Tilbehørspakke')
                                  ? item.price_extra
                                  : '40 kr. pr. kuvert'}
                              </span>
                              <span className={`${isSelected ? 'text-[#C25E27] font-bold' : ''} whitespace-nowrap`}>
                                {isSelected ? 'Valgt' : 'Vælg kartoffel'}
                              </span>
                            </div>
                          </div>
                        )
                      })}
                    </div>

                    <div className="mt-8 pt-6 border-t border-[#E6DFD5] flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="inline-flex items-center gap-2 bg-[#FAF8F5] hover:bg-[#F3EFEA] text-[#241F1C] border border-[#E6DFD5] px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer"
                      >
                        <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                        <span>Tilbage</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleNext}
                        className="inline-flex items-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-5 py-3 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer shadow-sm"
                      >
                        <span>Videre til Salater (Trin 4)</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}

                {/* TRIN 4: SALATER */}
                {currentStep === 4 && (
                  <div className="bg-white rounded-2xl p-6 sm:p-8 border border-[#E6DFD5] shadow-sm space-y-6">
                    <div className="pb-6 border-b border-[#E6DFD5]">
                      <span className="text-xs uppercase font-bold text-[#241F1C] tracking-wider">
                        Trin 4 af 4 • Vælg salater
                      </span>
                      <h2 className="text-xl sm:text-2xl font-bold text-[#241F1C]">Vælg Salater</h2>
                      <p className="text-sm text-[#6E635C] mt-1">
                        Vælg friske, velsmagende salater til din menu. Hold musen over salaten for at se ingredienserne.
                      </p>
                    </div>

                    {/* Salat Valgmuligheder */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* 1. Almindelig grøn salat */}
                      {(() => {
                        const greenSalad = salads.find(
                          (s) => s.title?.toLowerCase().includes('grøn') || s.id === 'salads-groen'
                        ) || {
                          id: 'salads-groen',
                          title: 'Almindelig grøn salat',
                          ingredients: [
                            'Icebergsalat',
                            'Ærter',
                            'Tomat',
                            'Gulerod',
                            'Majs',
                            'Agurk',
                            'Croutoner/peanuts',
                          ],
                        }
                        const isSelected = selectedSalads.some((i) => i.id === greenSalad.id)
                        const itemImg = Array.isArray(greenSalad.image) ? greenSalad.image[0] : greenSalad.image || '/_assets/stemningsfuld-buffet-med-helstegt-steg.png'
                        const ingredientsList = [
                          'Icebergsalat',
                          'Ærter',
                          'Tomat',
                          'Gulerod',
                          'Majs',
                          'Agurk',
                          'Croutoner/peanuts',
                        ]

                        return (
                          <div
                            key={greenSalad.id}
                            className={`group relative rounded-xl p-5 sm:p-6 border-2 transition-all flex flex-col justify-between ${
                              isSelected
                                ? 'border-[#C25E27] bg-[#FAF8F5] shadow-sm'
                                : 'border-[#E6DFD5] bg-white hover:border-[#DDD5C9] hover:bg-[#FAF8F5]/40'
                            }`}
                          >
                            <div>
                              <div className="flex items-start gap-3.5">
                                {itemImg && (
                                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden shrink-0 border border-[#E6DFD5] bg-[#FAF8F5]">
                                    <img
                                      src={imageUrl(itemImg, 'width=240,quality=85,format=auto')}
                                      alt={greenSalad.title || 'Almindelig grøn salat'}
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                    />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <span className="text-[11px] font-bold text-[#C25E27] block">
                                    Nr. 100
                                  </span>
                                  <h3 className="font-bold text-lg text-[#241F1C]">
                                    Almindelig grøn salat
                                  </h3>
                                  <p className="text-xs text-[#6E635C] mt-1">
                                    Frisk og sprød blandet salat.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => toggleItem(greenSalad, selectedSalads, setSelectedSalads)}
                                  className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors cursor-pointer ${
                                    isSelected
                                      ? 'bg-[#C25E27] text-white'
                                      : 'border-2 border-[#DDD5C9] bg-white text-transparent hover:border-[#C25E27]'
                                  }`}
                                  aria-label="Vælg Almindelig grøn salat"
                                >
                                  <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                                </button>
                              </div>

                              {/* HOVER INFOTEKST BOKS FOR ALM. GRØN SALAT */}
                              <div className="mt-4 pt-4 border-t border-[#E6DFD5]">
                                <div className="relative group/tooltip">
                                  <div className="flex items-center justify-between bg-[#FAF8F5] hover:bg-[#F3EFEA] border border-[#E6DFD5] hover:border-[#C25E27]/50 px-3.5 py-2.5 rounded-lg text-xs text-[#6E635C] transition-all cursor-pointer">
                                    <span className="flex items-center gap-2 font-medium text-[#241F1C]">
                                      <i className="fa-solid fa-circle-info text-[#C25E27]" aria-hidden="true" />
                                      <span>Hold musen over for ingredienser</span>
                                    </span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-[#6E635C] group-hover/tooltip:rotate-180 transition-transform" aria-hidden="true" />
                                  </div>

                                  {/* Tooltip popup: skjult som standard og vises udelukkende ved hover (og fokus) */}
                                  <div
                                    role="tooltip"
                                    className="absolute left-0 right-0 top-full mt-2 z-30 p-4 bg-white border-2 border-[#C25E27] rounded-xl shadow-xl opacity-0 invisible pointer-events-none group-hover/tooltip:opacity-100 group-hover/tooltip:visible group-hover/tooltip:pointer-events-auto group-focus-within/tooltip:opacity-100 group-focus-within/tooltip:visible group-focus-within/tooltip:pointer-events-auto transition-all duration-200"
                                  >
                                    <div className="font-bold text-[#241F1C] mb-2 flex items-center justify-between pb-2 border-b border-[#E6DFD5]">
                                      <span className="flex items-center gap-1.5">
                                        <i className="fa-solid fa-leaf text-[#C25E27]" aria-hidden="true" />
                                        <span>Ingredienser i Almindelig grøn salat:</span>
                                      </span>
                                    </div>
                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-[#241F1C]">
                                      {ingredientsList.map((ing) => (
                                        <li key={ing} className="flex items-center gap-1.5">
                                          <span className="w-1.5 h-1.5 rounded-full bg-[#C25E27] shrink-0" />
                                          <span>{ing}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-6 pt-3 border-t border-[#E6DFD5] flex items-center justify-between">
                              <span className="text-xs text-[#C25E27] font-semibold">
                                20 kr. pr. kuvert
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleItem(greenSalad, selectedSalads, setSelectedSalads)}
                                className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
                                  isSelected
                                    ? 'bg-[#C25E27] text-white'
                                    : 'bg-[#FAF8F5] hover:bg-[#F3EFEA] text-[#241F1C] border border-[#E6DFD5]'
                                }`}
                              >
                                {isSelected ? 'Valgt til menu' : 'Vælg denne salat'}
                              </button>
                            </div>
                          </div>
                        )
                      })()}

                      {/* 2. Sommersalat */}
                      {(() => {
                        const summerSalad = salads.find(
                          (s) => s.title?.toLowerCase().includes('sommer') || s.id === 'salads-sommer'
                        ) || {
                          id: 'salads-sommer',
                          title: 'Sommersalat',
                          ingredients: [
                            'Vandmelon og honningmelon',
                            'Rucola',
                            'Ristede sesamfrø',
                            'Tomater',
                            'Agurk',
                          ],
                        }
                        const isSelected = selectedSalads.some((i) => i.id === summerSalad.id)
                        const itemImg = Array.isArray(summerSalad.image) ? summerSalad.image[0] : summerSalad.image || '/_assets/sommersalat-melon.jpg'
                        const ingredientsList = [
                          'Vandmelon og honningmelon',
                          'Rucola',
                          'Ristede sesamfrø',
                          'Tomater',
                          'Agurk',
                        ]

                        return (
                          <div
                            key={summerSalad.id}
                            className={`relative rounded-xl p-5 sm:p-6 border-2 transition-all flex flex-col justify-between ${
                              isSelected
                                ? 'border-[#C25E27] bg-[#FAF8F5] shadow-sm'
                                : 'border-[#E6DFD5] bg-white hover:border-[#DDD5C9] hover:bg-[#FAF8F5]/40'
                            }`}
                          >
                            <div>
                              <div className="flex items-start gap-3.5">
                                {itemImg && (
                                  <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg overflow-hidden shrink-0 border border-[#E6DFD5] bg-[#FAF8F5]">
                                    <img
                                      src={imageUrl(itemImg, 'width=240,quality=85,format=auto')}
                                      alt={summerSalad.title || 'Sommersalat'}
                                      className="w-full h-full object-cover"
                                      loading="lazy"
                                    />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <span className="text-[11px] font-bold text-[#C25E27] block">
                                    Nr. 111
                                  </span>
                                  <h3 className="font-bold text-lg text-[#241F1C]">
                                    Sommersalat
                                  </h3>
                                  <p className="text-xs text-[#6E635C] mt-1">
                                    Frisk og sommerlig salat med melon og ristede sesamfrø.
                                  </p>
                                </div>
                                <button
                                  type="button"
                                  onClick={() => toggleItem(summerSalad, selectedSalads, setSelectedSalads)}
                                  className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 transition-colors cursor-pointer ${
                                    isSelected
                                      ? 'bg-[#C25E27] text-white'
                                      : 'border-2 border-[#DDD5C9] bg-white text-transparent hover:border-[#C25E27]'
                                  }`}
                                  aria-label="Vælg Sommersalat"
                                >
                                  <i className="fa-solid fa-check text-xs" aria-hidden="true" />
                                </button>
                              </div>

                              {/* INGREDIENSER FOR SOMMERSALAT - Vises ved hover/fokus i infoboks */}
                              <div className="mt-4 pt-4 border-t border-[#E6DFD5]">
                                <div className="relative group/tooltip inline-block w-full">
                                  <div
                                    tabIndex={0}
                                    className="p-3 bg-[#FAF8F5] hover:bg-[#F4EEE7] border border-[#E6DFD5] hover:border-[#C25E27] rounded-xl text-xs flex items-center justify-between cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#C25E27]/30"
                                  >
                                    <span className="font-semibold text-[#241F1C] flex items-center gap-1.5">
                                      <i className="fa-solid fa-circle-info text-[#C25E27]" aria-hidden="true" />
                                      <span>Hold musen her for at se ingredienser</span>
                                    </span>
                                    <i className="fa-solid fa-chevron-down text-[10px] text-[#6E635C] group-hover/tooltip:rotate-180 transition-transform" aria-hidden="true" />
                                  </div>

                                  {/* Tooltip popup: skjult som standard og vises udelukkende ved hover (og fokus) */}
                                  <div
                                    role="tooltip"
                                    className="absolute left-0 right-0 top-full mt-2 z-30 p-4 bg-white border-2 border-[#C25E27] rounded-xl shadow-xl opacity-0 invisible pointer-events-none group-hover/tooltip:opacity-100 group-hover/tooltip:visible group-hover/tooltip:pointer-events-auto group-focus-within/tooltip:opacity-100 group-focus-within/tooltip:visible group-focus-within/tooltip:pointer-events-auto transition-all duration-200"
                                  >
                                    <div className="font-bold text-[#241F1C] mb-2 flex items-center justify-between pb-2 border-b border-[#E6DFD5]">
                                      <span className="flex items-center gap-1.5">
                                        <i className="fa-solid fa-lemon text-[#C25E27]" aria-hidden="true" />
                                        <span>Ingredienser i Sommersalat:</span>
                                      </span>
                                    </div>
                                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-xs text-[#241F1C]">
                                      {ingredientsList.map((ing) => (
                                        <li key={ing} className="flex items-center gap-1.5">
                                          <span className="w-1.5 h-1.5 rounded-full bg-[#C25E27] shrink-0" />
                                          <span>{ing}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div className="mt-6 pt-3 border-t border-[#E6DFD5] flex items-center justify-between">
                              <span className="text-xs text-[#C25E27] font-semibold">
                                30 kr. pr. kuvert (+10 kr.)
                              </span>
                              <button
                                type="button"
                                onClick={() => toggleItem(summerSalad, selectedSalads, setSelectedSalads)}
                                className={`text-xs font-bold px-3 py-1.5 rounded-lg transition-colors cursor-pointer ${
                                  isSelected
                                    ? 'bg-[#C25E27] text-white'
                                    : 'bg-[#FAF8F5] hover:bg-[#F3EFEA] text-[#241F1C] border border-[#E6DFD5]'
                                }`}
                              >
                                {isSelected ? 'Valgt til menu' : 'Vælg denne salat'}
                              </button>
                            </div>
                          </div>
                        )
                      })()}
                    </div>

                    {/* FREMHÆVET PÅKRÆVET TEKST: "Ring til os for at sammensætte din egen salat." */}
                    <div className="mt-6 bg-[#FAF8F5] border-2 border-dashed border-[#C25E27]/40 rounded-2xl p-5 sm:p-6 text-center">
                      <div className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-[#C25E27]/10 text-[#C25E27] mb-2">
                        <i className="fa-solid fa-phone text-base" aria-hidden="true" />
                      </div>
                      <h4 className="text-base sm:text-lg font-bold text-[#241F1C]">
                        Ring til os for at sammensætte din egen salat.
                      </h4>
                      <p className="text-xs sm:text-sm text-[#6E635C] mt-1 max-w-lg mx-auto">
                        Har du særlige ønsker, allergier eller vil du kombinere dine helt egne yndlingsråvarer? Vi tilpasser altid gerne salatbuffeten til jeres arrangement.
                      </p>
                      <div className="mt-3">
                        <a
                          href="tel:25752724"
                          className="inline-flex items-center gap-2 bg-[#241F1C] hover:bg-[#38322E] text-white px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-colors"
                        >
                          <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                          <span>Ring på 25 75 27 24</span>
                        </a>
                      </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-[#E6DFD5] flex items-center justify-between gap-3">
                      <button
                        type="button"
                        onClick={handlePrev}
                        className="inline-flex items-center gap-2 bg-[#FAF8F5] hover:bg-[#F3EFEA] text-[#241F1C] border border-[#E6DFD5] px-4 py-2.5 sm:px-5 sm:py-3 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer"
                      >
                        <i className="fa-solid fa-arrow-left text-xs" aria-hidden="true" />
                        <span>Tilbage</span>
                      </button>

                      <button
                        type="button"
                        onClick={handleNext}
                        className="inline-flex items-center gap-2 bg-[#C25E27] hover:bg-[#A84E1C] text-white px-5 py-3 rounded-xl font-bold text-xs sm:text-sm transition-all cursor-pointer shadow-sm"
                      >
                        <span>Se opsummering &amp; samlet pris (Trin 5)</span>
                        <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                      </button>
                    </div>
                  </div>
                )}

                {/* TRIN 5: OPSUMMERING, SAMLET PRIS OG FORESPØRGSEL */}
                {currentStep === 5 && (
                  <div className="space-y-8">
                    {/* STOR TYDELIG PRIS- OG MENUOPSUMMERING */}
                    <div className="bg-[#241F1C] text-white rounded-2xl p-6 sm:p-8 shadow-md">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 pb-6 border-b border-[#38322E]">
                        <div>
                          <span className="text-xs uppercase font-bold text-[#C25E27] tracking-wider">
                            Trin 5 af 5 • Din sammensatte menu
                          </span>
                          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mt-1">
                            Menuopsummering &amp; Samlet Pris
                          </h2>
                          <p className="text-sm text-[#DDD5C9] mt-1">
                            Her er det fulde overblik over dine valg samt beregnet samlet pris for arrangementet.
                          </p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setCurrentStep(1)}
                          className="inline-flex items-center gap-1.5 text-xs text-[#FAF8F5] bg-[#38322E] hover:bg-[#4D4540] px-3.5 py-2 rounded-lg self-start sm:self-auto transition-colors cursor-pointer"
                        >
                          <i className="fa-solid fa-pen-to-square text-xs" aria-hidden="true" />
                          <span>Rediger menu</span>
                        </button>
                      </div>

                      {/* SAMLET PRIS FREMGÅR TYDELIGT */}
                      <div className="my-6 p-6 rounded-2xl bg-gradient-to-br from-[#38322E] to-[#241F1C] border-2 border-[#C25E27] shadow-lg">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                          <div>
                            <span className="text-xs uppercase font-bold text-[#C25E27] tracking-wider block">
                              Beregnet samlet pris
                            </span>
                            <div className="mt-1 flex items-baseline gap-2">
                              <span className="text-4xl sm:text-5xl font-black text-white tracking-tight">
                                {priceCalculation.pricePerGuest} kr.
                              </span>
                              <span className="text-base text-[#DDD5C9] font-medium">/ kuvert</span>
                            </div>
                            <p className="text-xs text-[#DDD5C9] mt-1">
                              Inkl. moms, tilberedning og anretning.
                            </p>
                          </div>

                          {/* Antal gæster vælger i opsummering */}
                          <div className="bg-[#241F1C]/80 p-4 rounded-xl border border-[#4D4540] flex flex-col sm:flex-row sm:items-center gap-4">
                            <div>
                              <label htmlFor="summary_guests" className="text-xs text-[#DDD5C9] block font-semibold mb-1">
                                Antal kuverter / gæster:
                              </label>
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setGuestCount((prev) => Math.max(10, (Number(prev) || 35) - 5))}
                                  className="w-8 h-8 rounded-lg bg-[#38322E] hover:bg-[#4D4540] text-white flex items-center justify-center font-bold text-sm cursor-pointer"
                                >
                                  -
                                </button>
                                <input
                                  type="number"
                                  id="summary_guests"
                                  min="10"
                                  value={guestCount}
                                  onChange={(e) => setGuestCount(Math.max(1, Number(e.target.value) || 0))}
                                  className="w-20 text-center font-bold text-lg bg-white text-[#241F1C] py-1 px-2 rounded-lg"
                                />
                                <button
                                  type="button"
                                  onClick={() => setGuestCount((prev) => (Number(prev) || 35) + 5)}
                                  className="w-8 h-8 rounded-lg bg-[#38322E] hover:bg-[#4D4540] text-white flex items-center justify-center font-bold text-sm cursor-pointer"
                                >
                                  +
                                </button>
                              </div>
                            </div>

                            <div className="sm:border-l sm:border-[#4D4540] sm:pl-4">
                              <span className="text-xs uppercase tracking-wider text-[#A89E96] block font-bold">
                                Samlet beløb ({guestCount} pers.)
                              </span>
                              <span className="text-2xl sm:text-3xl font-extrabold text-[#C25E27]">
                                {priceCalculation.totalPrice.toLocaleString('da-DK')} kr.
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Prisopgørelse / Detaljeret oversigt */}
                        <div className="mt-5 pt-4 border-t border-[#4D4540] grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs text-[#DDD5C9]">
                          <div className="bg-[#241F1C]/60 p-2.5 rounded-lg">
                            <span className="text-[#A89E96] block">Forretter ({selectedStarters.length}):</span>
                            <span className="font-bold text-white">
                              {priceCalculation.starterPrice > 0
                                ? `${priceCalculation.starterPrice} kr./kuvert`
                                : '0 kr. (ikke valgt)'}
                            </span>
                          </div>
                          <div className="bg-[#241F1C]/60 p-2.5 rounded-lg">
                            <span className="text-[#A89E96] block">Kød &amp; Stege ({selectedMeats.length}):</span>
                            <span className="font-bold text-white">
                              {priceCalculation.meatPrice > 0
                                ? `${priceCalculation.meatPrice} kr./kuvert`
                                : '0 kr. (vælg kød)'}
                            </span>
                          </div>
                          <div className="bg-[#241F1C]/60 p-2.5 rounded-lg">
                            <span className="text-[#A89E96] block">Kartofler &amp; Salater:</span>
                            <span className="font-bold text-white">
                              {priceCalculation.sidesPrice > 0
                                ? `${priceCalculation.sidesPrice} kr./kuvert`
                                : '0 kr. (ikke valgt)'}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Valgte retter specificeret */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-[#38322E]">
                        {/* Forret */}
                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-[#C25E27] mb-2 flex items-center justify-between">
                            <span>1. Forret ({selectedStarters.length})</span>
                            <button
                              type="button"
                              onClick={() => setCurrentStep(1)}
                              className="text-[11px] text-[#DDD5C9] hover:text-white underline cursor-pointer"
                            >
                              Rediger
                            </button>
                          </h3>
                          {selectedStarters.length > 0 ? (
                            <ul className="text-sm text-[#DDD5C9] space-y-1">
                              {selectedStarters.map((s) => (
                                <li key={s.id} className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#C25E27]" />
                                  <span className="font-medium text-white">{s.title}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-[#6E635C] italic">Ingen forret valgt (kan tilvælges)</p>
                          )}
                        </div>

                        {/* Kød */}
                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-[#C25E27] mb-2 flex items-center justify-between">
                            <span>2. Kød ({selectedMeats.length})</span>
                            <button
                              type="button"
                              onClick={() => setCurrentStep(2)}
                              className="text-[11px] text-[#DDD5C9] hover:text-white underline cursor-pointer"
                            >
                              Rediger
                            </button>
                          </h3>
                          {selectedMeats.length > 0 ? (
                            <ul className="text-sm text-[#DDD5C9] space-y-1">
                              {selectedMeats.map((m) => (
                                <li key={m.id} className="flex items-center gap-2">
                                  <span className="w-1.5 h-1.5 rounded-full bg-[#C25E27]" />
                                  <span className="font-medium text-white">{m.title}</span>
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <p className="text-xs text-[#6E635C] italic">Ingen kød valgt endnu</p>
                          )}
                        </div>

                        {/* Kartofler */}
                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-[#C25E27] mb-2 flex items-center justify-between">
                            <span>3. Kartofler ({selectedPotatoes.length})</span>
                            <button
                              type="button"
                              onClick={() => setCurrentStep(3)}
                              className="text-[11px] text-[#DDD5C9] hover:text-white underline cursor-pointer"
                            >
                              Rediger
                            </button>
                          </h3>
                          {selectedPotatoes.length > 0 ? (
                            <ul className="text-sm text-[#DDD5C9] space-y-1">
                              {selectedPotatoes.map((p) => {
                                const isFloede = p.title?.toLowerCase().includes('fløde')
                                const price = isFloede ? 35 : 40
                                return (
                                  <li key={p.id} className="flex items-center justify-between gap-2">
                                    <span className="flex items-center gap-2">
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#C25E27]" />
                                      <span className="text-white">{p.title}</span>
                                    </span>
                                    <span className="text-xs text-[#A89E96]">{price} kr. / kuvert</span>
                                  </li>
                                )
                              })}
                            </ul>
                          ) : (
                            <p className="text-xs text-[#6E635C] italic">Ingen kartofler valgt</p>
                          )}
                        </div>

                        {/* Salater */}
                        <div>
                          <h3 className="text-xs font-bold uppercase tracking-wider text-[#C25E27] mb-2 flex items-center justify-between">
                            <span>4. Salater ({selectedSalads.length})</span>
                            <button
                              type="button"
                              onClick={() => setCurrentStep(4)}
                              className="text-[11px] text-[#DDD5C9] hover:text-white underline cursor-pointer"
                            >
                              Rediger
                            </button>
                          </h3>
                          {selectedSalads.length > 0 ? (
                            <ul className="text-sm text-[#DDD5C9] space-y-1">
                              {selectedSalads.map((s) => {
                                const isSommer = s.title?.toLowerCase().includes('sommer')
                                return (
                                  <li key={s.id} className="flex items-center justify-between gap-2">
                                    <span className="flex items-center gap-2">
                                      <span className="w-1.5 h-1.5 rounded-full bg-[#C25E27]" />
                                      <span className="text-white">{s.title}</span>
                                    </span>
                                    <span className="text-xs text-[#A89E96]">
                                      {isSommer ? '30 kr. / kuvert' : '20 kr. / kuvert'}
                                    </span>
                                  </li>
                                )
                              })}
                            </ul>
                          ) : (
                            <p className="text-xs text-[#6E635C] italic">Ingen salater valgt</p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Integrated Contact & Inquiry Form */}
                    <div id="bestillings-formular" className="pt-2 space-y-6 scroll-mt-28">
                      <div className="mb-4">
                        <span className="text-xs uppercase font-bold text-[#C25E27] tracking-wider">
                          Afsluttende trin
                        </span>
                        <h3 className="text-2xl font-bold text-[#241F1C]">
                          Send uforpligtende bestillingsforespørgsel
                        </h3>
                        <p className="text-sm text-[#6E635C] mt-1">
                          Udfyld dine kontaktoplysninger herunder. Vi kontakter dig hurtigst muligt med bekræftelse og en
                          uforpligtende snak om dit arrangement.
                        </p>
                      </div>

                      <ContactForm
                        selectedMenu={{
                          starters: selectedStarters,
                          meats: selectedMeats,
                          potatoes: selectedPotatoes,
                          salads: selectedSalads,
                          pricePerGuest: priceCalculation.pricePerGuest,
                          totalPrice: priceCalculation.totalPrice,
                          guestCount: guestCount,
                        }}
                        onResetMenu={handleReset}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sticky Sidebar Summary (4 cols on desktop) */}
          <div className="lg:col-span-4 sticky top-28 space-y-4">
            <div className="bg-white rounded-2xl p-6 border border-[#E6DFD5] shadow-sm">
              <div className="flex items-center justify-between pb-4 border-b border-[#E6DFD5]">
                <h3 className="font-bold text-base text-[#241F1C]">
                  <span>Din Valgte Menu</span>
                </h3>
                {totalSelections > 0 && (
                  <button
                    type="button"
                    onClick={handleReset}
                    className="text-xs text-[#6E635C] hover:text-[#C25E27] underline cursor-pointer"
                  >
                    Nulstil
                  </button>
                )}
              </div>

              {/* Samlet pris fremhævet i sidebaren */}
              <div className="py-4 border-b border-[#E6DFD5] bg-[#FAF8F5] -mx-6 px-6">
                <span className="text-[11px] font-bold uppercase tracking-wider text-[#6E635C] block">
                  Beregnet samlet pris
                </span>
                <div className="flex items-baseline justify-between mt-1">
                  <div>
                    <span className="text-2xl sm:text-3xl font-black text-[#241F1C]">
                      {priceCalculation.pricePerGuest} kr.
                    </span>
                    <span className="text-xs text-[#6E635C] ml-1">/ kuvert</span>
                  </div>
                  {priceCalculation.pricePerGuest > 0 && (
                    <div className="text-right">
                      <span className="text-xs text-[#6E635C] block">Ved {guestCount} pers:</span>
                      <span className="text-sm font-bold text-[#C25E27]">
                        {priceCalculation.totalPrice.toLocaleString('da-DK')} kr.
                      </span>
                    </div>
                  )}
                </div>
              </div>

              <div className="py-4 space-y-3.5 text-xs text-[#241F1C]">
                {/* 1. Forretter */}
                <div>
                  <div className="flex items-center justify-between font-semibold text-[#6E635C] mb-1">
                    <span>1. Forretter</span>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      className="text-[#C25E27] hover:underline cursor-pointer"
                    >
                      Skift
                    </button>
                  </div>
                  {selectedStarters.length > 0 ? (
                    <div className="pl-2 border-l-2 border-[#C25E27] space-y-0.5 font-medium">
                      {selectedStarters.map((s) => (
                        <div key={s.id} className="flex justify-between">
                          <span>{s.title}</span>
                          <span className="text-[#6E635C]">43 kr.</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[#A89E96] italic">Ingen valgt</span>
                  )}
                </div>

                {/* 2. Kød */}
                <div>
                  <div className="flex items-center justify-between font-semibold text-[#6E635C] mb-1">
                    <span>2. Kød</span>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(2)}
                      className="text-[#C25E27] hover:underline cursor-pointer"
                    >
                      Skift
                    </button>
                  </div>
                  {selectedMeats.length > 0 ? (
                    <div className="pl-2 border-l-2 border-[#C25E27] space-y-0.5 font-medium">
                      {selectedMeats.map((m) => (
                        <div key={m.id}>{m.title}</div>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[#A89E96] italic">Ingen kød valgt endnu</span>
                  )}
                </div>

                {/* 3. Kartofler */}
                <div>
                  <div className="flex items-center justify-between font-semibold text-[#6E635C] mb-1">
                    <span>3. Kartofler</span>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(3)}
                      className="text-[#C25E27] hover:underline cursor-pointer"
                    >
                      Skift
                    </button>
                  </div>
                  {selectedPotatoes.length > 0 ? (
                    <div className="pl-2 border-l-2 border-[#C25E27] space-y-0.5 font-medium">
                      {selectedPotatoes.map((p) => {
                        const isFloede = p.title?.toLowerCase().includes('fløde')
                        const price = isFloede ? 35 : 40
                        return (
                          <div key={p.id} className="flex justify-between">
                            <span>{p.title}</span>
                            <span className="text-[#6E635C]">{price} kr.</span>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <span className="text-[#A89E96] italic">Ingen kartofler valgt</span>
                  )}
                </div>

                {/* 4. Salater */}
                <div>
                  <div className="flex items-center justify-between font-semibold text-[#6E635C] mb-1">
                    <span>4. Salater</span>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(4)}
                      className="text-[#C25E27] hover:underline cursor-pointer"
                    >
                      Skift
                    </button>
                  </div>
                  {selectedSalads.length > 0 ? (
                    <div className="pl-2 border-l-2 border-[#C25E27] space-y-0.5 font-medium">
                      {selectedSalads.map((s) => {
                        const isSommer = s.title?.toLowerCase().includes('sommer')
                        return (
                          <div key={s.id} className="flex justify-between">
                            <span>{s.title}</span>
                            <span className="text-[#6E635C]">
                              {isSommer ? '30 kr.' : '20 kr.'}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  ) : (
                    <span className="text-[#A89E96] italic">Ingen salater valgt</span>
                  )}
                </div>
              </div>

              {currentStep < 5 ? (
                <button
                  type="button"
                  onClick={() => setCurrentStep(5)}
                  className="w-full mt-4 bg-[#241F1C] hover:bg-[#38322E] text-white py-2.5 rounded-xl font-bold text-xs shadow-sm transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <span>Gå direkte til opsummering &amp; pris</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </button>
              ) : null}

              {/* Bestil nu knap */}
              <button
                type="button"
                onClick={handleOrderNow}
                className="w-full mt-3 bg-[#C25E27] hover:bg-[#A84E1C] text-white py-2.5 rounded-xl font-bold text-xs shadow-sm transition-colors flex items-center justify-center gap-2 cursor-pointer"
              >
                <span>Bestil nu</span>
                <i className="fa-solid fa-arrow-down text-[10px]" aria-hidden="true" />
              </button>

              {/* Add to Festkurv directly from sidebar */}
              {selectedMeats.length > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setMenuInCart({
                      starters: selectedStarters,
                      meats: selectedMeats,
                      potatoes: selectedPotatoes,
                      salads: selectedSalads,
                      pricePerGuest: priceCalculation.pricePerGuest,
                      totalPrice: priceCalculation.totalPrice,
                      guestCount: guestCount,
                    })
                  }}
                  className="w-full mt-2 bg-[#FAF8F5] hover:bg-[#F3EFEA] text-[#241F1C] border border-[#E6DFD5] py-2 rounded-xl font-bold text-xs shadow-sm transition-colors flex items-center justify-center gap-2 cursor-pointer"
                >
                  <i className="fa-solid fa-basket-shopping text-xs text-[#C25E27]" aria-hidden="true" />
                  <span>Gem menu i Festkurv</span>
                </button>
              )}
            </div>

            {/* Quick Contact Help Box */}
            <div className="bg-white rounded-2xl p-5 border border-[#E6DFD5] space-y-2">
              <div className="flex items-center gap-2 text-[#C25E27] font-bold text-xs uppercase tracking-wide">
                <i className="fa-solid fa-headset" aria-hidden="true" />
                <span>Har du spørgsmål til menuen?</span>
              </div>
              <p className="text-xs text-[#6E635C] leading-relaxed">
                Ring direkte til os for vejledning om kuverter, allergener eller sammensætning af selskabsmenu.
              </p>
              <div className="pt-1">
                <a
                  href="tel:25752724"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#241F1C] hover:text-[#C25E27] whitespace-nowrap transition-colors"
                >
                  <i className="fa-solid fa-phone text-[#C25E27] text-xs" aria-hidden="true" />
                  <span>25 75 27 24</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
